import React, { useEffect, useState } from 'react';
import { BookOpen, ArrowDown, AlertTriangle, FilePlus2, X } from 'lucide-react';
import { useLanguage, useT } from '../i18n';
import examplesFile from '../data/operation-examples.json';

const EXAMPLES = examplesFile.operations;

/**
 * The worked example behind each operation.
 *
 * The tables come from src/data/operation-examples.json, which is not
 * decoration: tests/test_examples_sprint11.py rebuilds each `before`, calls the
 * real backend function with the documented arguments and requires the result
 * to be exactly the `after` shown here. An example that stops being true fails
 * the build rather than misleading someone quietly.
 */

/** A missing value has to look like one — an empty cell reads as a bug. */
const EMPTY = '—';

const Cell = ({ value }) => {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground/60">{EMPTY}</span>;
  }
  return <>{String(value)}</>;
};

const ExampleTable = ({ table }) => (
  <div className="overflow-x-auto rounded-[var(--radius)] border border-border">
    <table className="w-full text-xs">
      <thead className="bg-muted/60">
        <tr>
          {table.columns.map((column) => (
            <th key={column} className="px-3 py-2 text-left font-medium whitespace-nowrap">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIndex) => (
          <tr key={rowIndex} className="border-t border-border">
            {row.map((value, cellIndex) => (
              <td key={cellIndex} className="px-3 py-1.5 whitespace-nowrap">
                <Cell value={value} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
      {title}
    </p>
    {children}
  </div>
);

/**
 * The link, plus the dialog it opens.
 *
 * `variant` only changes how the trigger looks: a discreet line under an
 * operation card, or a button next to the title once the form is open. Both
 * show the same thing, because someone who skipped the example on the card is
 * exactly the person who needs it while filling the form in.
 *
 * The overlay is the same hand-rolled one the rest of the app uses rather than
 * the Radix dialog sitting unused in ui/: that one is 24 kB gzipped for a
 * single modal, and it would leave two different modal idioms in a file that
 * already contains one. It sits above the operation modal it opens from, which
 * is why the z-index is explicit.
 */
const OperationExample = ({ operationId, operationName, variant = 'card' }) => {
  const t = useT();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      // The operation modal closes on Escape too, and it must not close
      // underneath: this dialog was opened last, so it is the one that goes.
      event.stopPropagation();
      setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [open]);

  const example = EXAMPLES[operationId];
  if (!example) return null;

  const text = (field) => example[field]?.[language] ?? example[field]?.en ?? '';

  const triggerClassName =
    variant === 'panel'
      ? 'btn-ghost h-8 px-2 text-sm flex items-center gap-1.5'
      : 'inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClassName}
        data-testid={`example-open-${operationId}`}
      >
        <BookOpen className={variant === 'panel' ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
        {t('example.link')}
      </button>

      {open && (
        <div
          className="modal-overlay"
          style={{ zIndex: 60 }}
          onClick={() => setOpen(false)}
          role="presentation"
        >
          <div
            className="modal-content"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={t('example.title', { operation: operationName })}
            data-testid={`example-dialog-${operationId}`}
          >
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <h3 className="text-lg font-semibold">
                  {t('example.title', { operation: operationName })}
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="btn-ghost h-8 w-8 p-0 flex-shrink-0"
                  aria-label={t('operations.close')}
                  data-testid={`example-close-${operationId}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <p className="text-sm text-muted-foreground">{text('summary')}</p>

              <Section title={t('example.before')}>
                <ExampleTable table={example.before} />
              </Section>

              {example.second && (
                <Section
                  title={
                    <span className="inline-flex items-center gap-1.5">
                      <FilePlus2 className="h-3.5 w-3.5" />
                      {t('example.secondFile')}
                    </span>
                  }
                >
                  <ExampleTable table={example.second} />
                </Section>
              )}

              <div className="flex justify-center">
                <ArrowDown className="h-5 w-5 text-primary" />
              </div>

              <Section title={t('example.after')}>
                <ExampleTable table={example.after} />
              </Section>

              <div className="flex items-start gap-2.5 rounded-[var(--radius)] border border-amber-200 bg-amber-50 p-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-amber-900 mb-0.5">
                    {t('example.gotcha')}
                  </p>
                  <p className="text-xs text-amber-900/90">{text('gotcha')}</p>
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
                {t('example.replayed')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OperationExample;
